import { useCallback, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { ApiError } from '../lib/api'
import { login, loginTotp, logout } from '../lib/auth'
import { describeApiError } from '../lib/errors'
import { useFocusTrap } from '../hooks/useFocusTrap'
import { useSession } from '../session/SessionProvider'
import { FormError } from './AuthShell'
import { OtpInput } from './OtpInput'
import { PasswordField } from './PasswordField'

/**
 * Oturum bitince MEVCUT GORUNUMUN UZERINE acilan yeniden dogrulama.
 *
 * ## Neden hard redirect DEGIL
 *
 * Bu, merkezsizlestirilmis tasarimin odulu. Run SUNUCUYA ait ve istemcinin
 * oturumundan bagimsiz uretmeye devam ediyor. `/login`e yonlendirmek:
 *   * kullanicinin YERINI kaybettirir (hangi sohbetteydi),
 *   * DRAFT'ini siler,
 *   * ve en kotusu, hala uretilmekte olan yanitin izini kaybettirir.
 *
 * Modal'da yeniden dogrulama basarili olunca cagiran `streamRun(runId,
 * after=lastSeq)`i tekrar cagiriyor ve modal ACIKKEN uretilen token'lar tek
 * bir ANINDA `resume` cercevesiyle geliyor. Kullanici hicbir sey kaybetmiyor.
 *
 * ## Kapatilamaz
 *
 * `Esc` ve backdrop bu modal'i KAPATMIYOR (odak tuzagindaki `onClose` bos):
 * arkadaki arayuz zaten calismaz durumda ve "kapat"a basmak kullaniciya
 * bozuk bir uygulama gosterirdi. Cikis icin acik bir buton var.
 */
export function ReauthModal() {
  const { t } = useTranslation()
  const { state, reauthDone } = useSession()
  const navigate = useNavigate()
  const container = useRef<HTMLDivElement>(null)
  const [stage, setStage] = useState<'password' | 'totp'>('password')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Kapatma yok: `onClose` bilincli olarak bos. Odak tuzagi yine gerekli -
  // arkadaki forma sekmelemek, gorunmeyen bir alana yazi yazdirirdi.
  const noClose = useCallback(() => {}, [])
  useFocusTrap(container, { active: true, onClose: noClose })

  const email = state?.user?.email ?? ''

  /**
   * `submitted`: otomatik gonderimde BAYAT state hatasina karsi
   * (gerekce `VerifyScreen`de).
   */
  async function submit(event?: React.FormEvent, submitted?: string) {
    event?.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      if (stage === 'password') {
        const result = await login(email, password)
        if (result.next === 'totp') {
          setStage('totp')
          return
        }
        reauthDone()
        return
      }
      await loginTotp(submitted ?? code)
      reauthDone()
    } catch (cause) {
      // 409 = "oturum zaten dogrulanmis": baska bir sekme tamamlamis
      // olabilir. Hata DEGIL - modal kapanmali, yoksa gercekte giris yapmis
      // bir kullaniciya hata gosterirdik.
      if (cause instanceof ApiError && cause.status === 409) {
        reauthDone()
        return
      }
      setError(describeApiError(cause, t))
      setCode('')
    } finally {
      setBusy(false)
    }
  }

  // Portal: mesaj listesinin `overflow`/`transform` baglami modal'i
  // KIRPARDI. Kok seviyesine render etmek bunu yapisal olarak engelliyor.
  return createPortal(
    <div className="modal-backdrop">
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reauth-title"
        ref={container}
        tabIndex={-1}
      >
        <h2 id="reauth-title">{t('auth.reauth.title')}</h2>
        <p className="gate-sub">{t('auth.reauth.body')}</p>

        <form onSubmit={submit} noValidate>
          <FormError message={error} />

          {stage === 'password' ? (
            <>
              <p className="field-hint">{email}</p>
              <PasswordField
                label={t('auth.field.password')}
                value={password}
                onChange={setPassword}
                autoComplete="current-password"
              />
            </>
          ) : (
            <OtpInput
              value={code}
              onChange={setCode}
              onComplete={(value) => void submit(undefined, value)}
              disabled={busy}
              invalid={Boolean(error)}
            />
          )}

          <button type="submit" className="btn-primary" disabled={busy}>
            {busy ? t('auth.reauth.submitBusy') : t('auth.reauth.continue')}
          </button>
          {/*
            Cikis: `logout` + `refresh`, sonra guard kullaniciyi `/login`e
            atiyor. Elle bir `href="/app/login"` yazmak `basename`i sabitler
            ve dev (Vite) ile uretim yollarinin ayrismasina yol acardi.
          */}
          <button
            type="button"
            className="btn-quiet"
            onClick={async () => {
              // Sunucu 401 verdiyse oturum zaten olu; `logout` yine de
              // cagriliyor ki cookie temizlensin ve modal geri gelmesin.
              await logout().catch(() => undefined)
              reauthDone()
              navigate('/login', { replace: true })
            }}
          >
            {t('auth.reauth.logout')}
          </button>
        </form>
      </div>
    </div>,
    document.body,
  )
}
